import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Shield, Users, Monitor, FileText, ChevronLeft, ExternalLink, Mail, BarChart3, Lock } from 'lucide-react';

const LOGO = '/images/edited-dsb-logo.png';
const HERO_IMAGE = 'https://images.pexels.com/photos/8386440/pexels-photo-8386440.jpeg?auto=compress&cs=tinysrgb&w=1200';

export default function DSBBusiness() {
  return (
    <main data-testid="dsb-business-page" className="pt-20">
      {/* Hero */}
      <section className="relative h-[60vh] min-h-[480px] overflow-hidden">
        <img src={HERO_IMAGE} alt="DSB Business Solutions Beratung" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0F1A2E]/75"></div>
        <div className="relative z-10 container-main h-full flex flex-col justify-end pb-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Link to="/unternehmen" className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm uppercase tracking-wider mb-6">
              <ChevronLeft size={16} /> Alle Unternehmen
            </Link>
            <img src={LOGO} alt="DSB Business Solutions GmbH" className="h-16 md:h-20 w-auto mb-6 brightness-0 invert" />
            <p className="text-lg md:text-xl text-white/80 max-w-xl leading-relaxed">
              Umfassende Beratungsdienstleistungen für den deutschen Mittelstand.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Über uns */}
      <section className="section-spacing bg-[#0A1220]">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <div className="accent-line mb-6"></div>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-6">
                Über DSB Business Solutions
              </h2>
              <p className="text-white/70 leading-relaxed mb-6">
                DSB Business Solutions GmbH bietet umfassende Beratungsdienstleistungen für den deutschen 
                Mittelstand. Unser Expertenteam unterstützt Sie in allen Bereichen der Unternehmensführung – 
                von Datenschutz über IT-Sicherheit bis hin zu Personalentwicklung und digitaler Transformation.
              </p>
              <p className="text-white/70 leading-relaxed mb-8">
                Als Tochterunternehmen der Basic United Holding GmbH vereinen wir Branchenexpertise mit 
                einem ganzheitlichen Beratungsansatz. Wir verstehen die Herausforderungen des Mittelstands 
                und entwickeln praxisnahe Lösungen, die nachhaltig wirken.
              </p>
              <div className="p-6 bg-[#0F1A2E]/5 border-l-4 border-[#0F1A2E]">
                <p className="text-sm text-white/50 uppercase tracking-wider mb-1">Geschäftsführung</p>
                <p className="font-semibold text-white">Admir Basic & Alessandro Costantino</p>
                <p className="text-sm text-white/70">Geschäftsführende Gesellschafter</p>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }}>
              <div className="grid grid-cols-2 gap-6">
                {[
                  { icon: Shield, label: 'DSGVO-Compliance', desc: 'Datenschutz rechtssicher umgesetzt' },
                  { icon: Lock, label: 'IT-Sicherheit', desc: 'Schutz vor Cyberbedrohungen' },
                  { icon: Users, label: 'HR-Beratung', desc: 'Personalentwicklung & Recruiting' },
                  { icon: Monitor, label: 'Digitalisierung', desc: 'Digitale Transformation begleiten' },
                ].map((item, i) => (
                  <div key={i} className="p-6 bg-[#0F1A2E] border border-[#1e3a5f]">
                    <item.icon size={24} className="text-[#F5A623] mb-4" />
                    <h4 className="font-semibold text-white text-sm mb-1">{item.label}</h4>
                    <p className="text-xs text-white/50">{item.desc}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Leistungen */}
      <ServicesSection />

      {/* Warum DSB */}
      <WhySection />

      {/* CTA */}
      <section className="bg-[#0F1A2E] py-20">
        <div className="container-main text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
              Beratungsbedarf? Sprechen Sie uns an.
            </h2>
            <p className="text-lg text-white/80 mb-8 max-w-xl mx-auto">
              Vereinbaren Sie ein unverbindliches Erstgespräch – wir analysieren Ihren Bedarf und entwickeln passgenaue Lösungen.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a href="mailto:info@dsb-deutschland.org" className="inline-flex items-center justify-center gap-2 bg-[#0A1220] text-[#0F1A2E] px-8 py-4 text-sm tracking-widest uppercase font-semibold hover:bg-[#F5A623] hover:text-white transition-colors">
                <Mail size={16} /> Kontakt aufnehmen
              </a>
              <a href="https://www.dsb-deutschland.org" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 border border-white text-white px-8 py-4 text-sm tracking-widest uppercase font-semibold hover:bg-[#0A1220] hover:text-[#0F1A2E] transition-colors">
                <ExternalLink size={16} /> Website besuchen
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}

function ServicesSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const services = [
    { icon: Shield, title: 'Datenschutz & DSGVO', desc: 'Vollumfängliche Beratung und Umsetzung aller datenschutzrechtlichen Anforderungen. Wir begleiten Sie von der Bestandsaufnahme bis zur laufenden Betreuung.', features: ['Datenschutz-Audits', 'Verarbeitungsverzeichnis', 'Externer Datenschutzbeauftragter', 'Mitarbeiterschulungen'] },
    { icon: Lock, title: 'IT-Sicherheit', desc: 'Schützen Sie Ihr Unternehmen vor Cyberangriffen und Datenverlusten. Wir entwickeln maßgeschneiderte Sicherheitskonzepte für Ihre IT-Infrastruktur.', features: ['Sicherheitsanalysen', 'Awareness-Schulungen', 'Notfallpläne', 'Penetrationstests'] },
    { icon: Users, title: 'HR-Beratung', desc: 'Strategische Personalberatung für den Mittelstand. Wir unterstützen Sie bei der Gewinnung, Entwicklung und Bindung von Talenten.', features: ['Recruiting-Strategien', 'Personalentwicklung', 'Employer Branding', 'Arbeitsrecht-Beratung'] },
    { icon: Monitor, title: 'IT-Consulting', desc: 'Digitale Transformation ganzheitlich gedacht. Wir begleiten Sie bei der Modernisierung Ihrer IT-Landschaft und Geschäftsprozesse.', features: ['Digitalisierungsstrategie', 'Prozessautomatisierung', 'Cloud-Migration', 'Software-Auswahl'] },
    { icon: BarChart3, title: 'Organisationsentwicklung', desc: 'Strukturen und Prozesse optimieren für nachhaltiges Wachstum. Wir analysieren, beraten und begleiten die Umsetzung.', features: ['Prozessoptimierung', 'Change Management', 'Strategieberatung', 'Lean Management'] },
    { icon: FileText, title: 'Compliance', desc: 'Rechtssichere Unternehmensführung in allen regulatorischen Bereichen. Wir sorgen dafür, dass Sie stets auf der sicheren Seite sind.', features: ['Compliance-Management', 'Risikobewertungen', 'Whistleblower-Systeme', 'Schulungen'] },
  ];

  return (
    <section ref={ref} className="section-spacing bg-[#0F1A2E]">
      <div className="container-main">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5 }} className="text-center mb-16">
          <div className="flex justify-center mb-6"><div className="accent-line"></div></div>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-4">Unsere Beratungsfelder</h2>
          <p className="text-lg text-white/70 max-w-2xl mx-auto">Ganzheitliche Lösungen für die Herausforderungen des Mittelstands.</p>
        </motion.div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.08 * (i + 1) }}
              className="bg-[#0A1220] p-8 border border-[#1e3a5f] hover:shadow-xl hover:border-[#F5A623]/30 transition-all duration-500">
              <s.icon size={28} className="text-[#0F1A2E] mb-4" />
              <h3 className="font-heading text-lg font-semibold text-white mb-3">{s.title}</h3>
              <p className="text-white/70 text-sm leading-relaxed mb-5">{s.desc}</p>
              <ul className="space-y-2">
                {s.features.map((f, j) => (
                  <li key={j} className="flex items-center gap-2 text-sm text-white/70">
                    <CheckCircle size={14} className="text-[#F5A623] flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhySection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const reasons = [
    { number: '01', title: 'Praxisnähe', desc: 'Unsere Berater kennen den Mittelstand aus eigener Erfahrung und entwickeln Lösungen, die wirklich funktionieren.' },
    { number: '02', title: 'Ganzheitlich', desc: 'Wir denken über Fachgrenzen hinaus und verknüpfen Datenschutz, IT, HR und Organisation zu einem stimmigen Gesamtkonzept.' },
    { number: '03', title: 'Nachhaltig', desc: 'Unsere Beratung zielt auf langfristige Wirksamkeit. Wir begleiten Sie auch nach der Projektphase.' },
  ];

  return (
    <section ref={ref} className="section-spacing bg-[#0A1220]">
      <div className="container-main">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5 }} className="mb-12">
          <div className="accent-line mb-6"></div>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-4">Warum DSB?</h2>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-8">
          {reasons.map((r, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.1 * (i + 1) }}>
              <span className="text-5xl font-heading font-bold text-white/10">{r.number}</span>
              <h3 className="font-heading text-xl font-semibold text-white mt-2 mb-3">{r.title}</h3>
              <p className="text-white/70 leading-relaxed">{r.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
